import { PATIENTS_GENDER } from '@constants/extension-codes';

import { calculateAge } from '@utils/date';
import { formatFhirDate, getFhirDate } from '@utils/fhir';
import { formatEmergencyContact, formatName, formatPhone } from '@utils/format';

import { PhoneContactUse } from 'generated/types';
import type { ContactPoint, PatientModel } from 'generated/types';
import type {
  Gender,
  PatientModelInput,
  PhoneContactInput,
} from 'generated/types';

import { Contact } from './contact';
import { FhirResource } from './fhir-resource';

export type PhoneUse = 'mobile' | 'home' | 'work' | 'fax';

export const PHONE_USES: PhoneUse[] = ['mobile', 'home', 'work', 'fax'];

/**
 * `ContactPoint.use` is a free string on the wire and FHIR allows values we
 * don't offer (`temp`, `old`), so anything read off a resource has to be
 * narrowed rather than cast. An unrecognised use falls back to Mobile, which
 * is what `toContactUse` would write it back as anyway.
 */
export function toPhoneUse(use: string | null | undefined): PhoneUse {
  return PHONE_USES.includes(use as PhoneUse) ? (use as PhoneUse) : 'mobile';
}

export type PhoneFormEntry = {
  id: string;
  value: string;
  use: PhoneUse;
  isPrimary: boolean;
};

/**
 * A row as it can actually sit in Formik mid-edit, which is looser than
 * `PhoneFormEntry` claims. `react-phone-number-input` calls
 * `onChange(undefined)` for a cleared or still-incomplete number and
 * `FormikTelField` writes that through unchanged, and `use` is only as
 * trustworthy as whatever put the row in form state. Anything reading straight
 * off the form has to cope with both.
 */
export type DraftPhoneFormEntry = Omit<PhoneFormEntry, 'use' | 'value'> & {
  use: string;
  value: string | undefined;
};

const PHONE_USE_TO_CONTACT_USE: Record<PhoneUse, PhoneContactUse> = {
  mobile: PhoneContactUse.Mobile,
  home: PhoneContactUse.Home,
  work: PhoneContactUse.Work,
  fax: PhoneContactUse.Fax,
};

/**
 * `use` is chosen from a dropdown constrained to PHONE_USES, so the lookup
 * hits. The fallback keeps an unexpected value from sending `undefined` for a
 * non-null field, which surfaces as an opaque GraphQL error rather than a
 * wrong phone type.
 */
function toContactUse(use: string): PhoneContactUse {
  // Indexed through a widened view rather than a cast to PhoneUse, so a miss
  // stays visible to the type checker. The record itself is still exhaustive,
  // so a new PhoneUse won't compile until it's mapped.
  const byUse: Record<string, PhoneContactUse | undefined> =
    PHONE_USE_TO_CONTACT_USE;
  return byUse[use] ?? PhoneContactUse.Mobile;
}

/**
 * The row a patient form starts with. Defaults to Mobile per VEG-779; the type
 * dropdown changes it. A function rather than a constant so two members can't
 * end up sharing one object.
 */
export const newPhoneEntry = (): PhoneFormEntry => ({
  id: 'initial-0',
  value: '',
  use: 'mobile',
  isPrimary: true,
});

/**
 * Puts the primary entry first. Both the creation input and `PatientRecord`'s
 * telecom setter encode the same backend contract, that rank follows position,
 * so they sort the same way.
 */
function sortPrimaryFirst<T extends { isPrimary: boolean }>(
  entries: readonly T[]
): T[] {
  return [...entries].sort(
    (a, b) => (b.isPrimary ? 1 : 0) - (a.isPrimary ? 1 : 0)
  );
}

/**
 * Turns the form's phone rows into the `phones` input the patient creation
 * mutations take, replacing the deprecated `primary_phone`.
 *
 * Rows the curator left blank are dropped, since the creation forms render an
 * empty first row whether or not a number is being entered. A row whose
 * `value` is `undefined` counts as blank (see `DraftPhoneFormEntry`). Returns
 * `undefined` when nothing survives, so callers can spread the result and
 * leave `phones` off the mutation entirely rather than sending `[]`.
 *
 * The primary entry sorts first because the backend's `buildPhoneTelecom`
 * assigns rank by position, and `PatientRecord.phones` reads rank back as
 * primary. If the primary row was the blank one, the first surviving row
 * takes over — the backend would rank it 1 regardless, so saying so here
 * keeps the sent payload honest.
 */
export function toPhoneContactInputs(
  entries: readonly DraftPhoneFormEntry[] | null | undefined
): PhoneContactInput[] | undefined {
  const filled = (entries ?? [])
    .map((entry) => ({ ...entry, value: entry.value?.trim() ?? '' }))
    .filter((entry) => entry.value);
  if (filled.length === 0) {
    return undefined;
  }
  return sortPrimaryFirst(filled).map((entry, index) => ({
    value: entry.value,
    use: toContactUse(entry.use),
    isPrimary: index === 0,
  }));
}

export type FormValues = Pick<
  PatientRecord,
  | 'address'
  | 'address2'
  | 'birthday'
  | 'city'
  | 'country'
  | 'gender'
  | 'phones'
  | 'postalCode'
  | 'region'
  | 'secondAddress'
  | 'secondAddressUnit'
  | 'secondCity'
  | 'secondCountry'
  | 'secondPostalCode'
  | 'secondRegion'
  | 'sex'
> & {
  submit: string | null;
};
export class PatientRecord extends Contact {
  public model: PatientModel;

  public constructor(model: PatientModel) {
    super(model);
    this.model = model;
  }

  public get sex() {
    return this.model.gender;
  }

  public set sex(value: string | null | undefined) {
    this.model = {
      ...this.model,
      gender: value ? (value as Gender) : null,
    };
  }

  public get gender() {
    const extension = this.findExtension(PATIENTS_GENDER);
    return extension?.valueString;
  }

  public set gender(value: string | null | undefined) {
    if (value) {
      this.addOrReplaceExtension({
        url: PATIENTS_GENDER,
        valueString: value,
      });
    } else {
      this.removeExtension(PATIENTS_GENDER);
    }
  }

  public get birthday() {
    const { birthDate } = this.model;
    return birthDate;
  }

  public set birthday(value: string | null | undefined) {
    this.model = {
      ...this.model,
      birthDate: value,
    };
  }

  // Every entry the patient has, not just the first — telecom.system is
  // 'phone' or 'fax', ordered by rank (see buildPhoneTelecom on the
  // backend, which this mirrors). Fax entries have no `use` on the wire,
  // so they map back to the 'fax' UI type by system alone.
  public get phones(): PhoneFormEntry[] {
    const entries = (this.model.telecom ?? []).filter(
      (entry): entry is ContactPoint & { value: string } =>
        Boolean(entry?.value) &&
        (entry?.system === 'phone' || entry?.system === 'fax')
    );
    const sorted = [...entries].sort(
      (a, b) => (a.rank ?? Infinity) - (b.rank ?? Infinity)
    );
    return sorted.map((entry, index) => ({
      id: `${entry.system}-${index}`,
      value: entry.value,
      use: entry.system === 'fax' ? 'fax' : toPhoneUse(entry.use),
      isPrimary: index === 0,
    }));
  }

  public set phones(value: PhoneFormEntry[] | null | undefined) {
    // Non-phone/fax entries (email) are left untouched.
    const contact = this.model.telecom?.filter(
      (entry) => entry?.system !== 'phone' && entry?.system !== 'fax'
    );
    // The primary entry sorts first so it gets rank 1, matching how the
    // backend orders phones when it builds telecom from this same shape.
    const sorted = sortPrimaryFirst(value ?? []);
    this.model = {
      ...this.model,
      telecom: [
        ...(contact ?? []),
        ...sorted.map((entry, index) => ({
          system: entry.use === 'fax' ? 'fax' : 'phone',
          value: entry.value,
          ...(entry.use !== 'fax' && { use: entry.use }),
          rank: index + 1,
        })),
      ] as ContactPoint[],
    };
  }

  public get practitioner() {
    const physicians = this.model.generalPractitioner;
    if (!physicians) {
      return null;
    }
    const [doctor] = physicians;
    const id = doctor?.reference;
    const name = doctor?.display;
    if (id && name) {
      return { id, name };
    }
    return null;
  }

  public get emergencyContact() {
    const [primary] = this.model.contact ?? [];
    if (!primary) {
      return null;
    }
    const { name, relationship, telecom } = primary;
    const contactName =
      formatName(
        name?.prefix?.join(' '),
        name?.given?.join(' '),
        name?.family,
        name?.suffix?.join(' ')
      ) ?? '';
    const relation = relationship?.[0]?.text ?? undefined;
    const phone = formatPhone(telecom?.[0]?.value);
    return formatEmergencyContact(contactName, relation, phone);
  }

  public get secondaryEmergencyContact() {
    const secondary = this.model.contact?.[1];
    if (!secondary) {
      return null;
    }
    const { name, relationship, telecom } = secondary;
    const contactName =
      formatName(
        name?.prefix?.join(' '),
        name?.given?.join(' '),
        name?.family,
        name?.suffix?.join(' ')
      ) ?? '';
    const relation = relationship?.[0]?.text ?? undefined;
    const phone = formatPhone(telecom?.[0]?.value);
    return formatEmergencyContact(contactName, relation, phone);
  }

  public get language() {
    return this.model.language;
  }

  public getBirthday() {
    const { birthDate } = this.model;
    return formatFhirDate(birthDate, { showTime: false, adjustTz: false });
  }

  /**
   * Get patient age in years
   * @returns Age in years
   */
  public getAge() {
    const date = getFhirDate(this.model.birthDate);
    if (!date) {
      return null;
    }
    return calculateAge(date);
  }

  public getModelInput(values?: FormValues): PatientModelInput {
    if (values) {
      for (const key in values) {
        try {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-expect-error
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          this[key] = values[key];
        } catch {
          // eslint-disable-next-line no-continue
          continue;
        }
      }
    }
    const cleaned = FhirResource.getCleanInput({
      ...this.model,
      managingOrganization: this.model.managingOrganization,
    });
    return cleaned;
  }
}
